<template>
  <div v-if="prevVal != null && !isNaN(prevVal)" class="range-comparison">
    <span :class="deltaClass">{{ deltaLabel }}</span>
    <span class="range-comparison-prev"> ({{ formattedPrev }})</span>
    <span v-if="showComparisonLabel" class="range-comparison-label ms-2"> {{ comparisonLabel }}</span>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

const props = defineProps({
  currVal: { type: Number, default: 0 },
  prevVal: { type: Number, default: null },
  // 'dollar' → $1.23, 'int' → 42, 'percent' → 12.3%, 'float' → 1.23
  format: { type: String, default: 'float' },
  showComparisonLabel: { type: Boolean, default: false },
  greenDirection: { type: String, default: 'up' },
})

const comparisonLabel = computed(() => {
  const mode = store.state.eshop.lastDateRange?.comparison || 'prev'
  return mode === 'yoy' ? 'vs. last year' : 'vs. prev period'
})

const pct = computed(() => {
  // prevVal must be a valid non-zero number (needed as divisor)
  if (props.prevVal == null || isNaN(props.prevVal) || props.prevVal === 0) return null
  // currVal must be a valid number (0 is legitimate — means 100% drop)
  if (props.currVal == null || isNaN(props.currVal)) return null
  return ((props.currVal - props.prevVal) / Math.abs(props.prevVal)) * 100
})

const deltaClass = computed(() => {
  if (pct.value === null) return 'text-muted range-comparison-delta'
  let upClass = 'text-success range-comparison-delta'
  let downClass = 'text-danger range-comparison-delta'
  if (props.greenDirection !== 'up') {
    upClass = 'text-danger range-comparison-delta'
    downClass = 'text-success range-comparison-delta'
  }
  return pct.value >= 0  ? upClass : downClass
})

const deltaLabel = computed(() => {
  if (pct.value === null) return '—'
  return `${pct.value >= 0 ? '▲' : '▼'} ${Math.abs(pct.value).toFixed(1)}%`
})

const formattedPrev = computed(() => {
  const v = props.prevVal
  if (v == null || isNaN(v)) return '—'
  switch (props.format) {
    case 'dollar':  return `$${parseFloat(v).toFixed(2)}`
    case 'int':     return `${parseInt(v)}`
    case 'percent': return `${parseFloat(v).toFixed(1)}%`
    default:        return `${parseFloat(v).toFixed(2)}`
  }
})
</script>

<style scoped>
.range-comparison {
  font-size: 0.72rem;
  margin-top: 2px;
  line-height: 1.2;
}
.range-comparison-delta {
  font-weight: 600;
}
.range-comparison-prev {
  color: var(--bs-secondary-color, #6c757d);
  font-size: 0.68rem;
}
.range-comparison-label {
  color: var(--bs-secondary-color, #6c757d);
  font-size: 0.65rem;
  font-style: italic;
}
</style>
